var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View, Animated, TextInput } from 'react-native';
import { useAnimation } from '../Animations/AnimationHooks';
import { DenseButtonView } from '../Buttons/DenseButtonView';
import { IconComponent } from '../Icons/IconComponent';
import { AnimatedPressable } from '../Buttons/AnimatedPressable';
import { AnimatedTextView } from '../Typography/AnimatedTextView';
import { component } from '../../Decorators/Component';
import { CoreConfig } from './../../../CoreConfig';
import { Configuration } from './../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { getDirection, useDirection } from '../../Hooks/UseDirection';
const CenterPosition = Platform.select({
    default: 16,
    android: 21,
    ios: 21
});
const TopPosition = Platform.select({
    default: -8,
    android: -2,
    ios: -2,
});
export let focusedRef = undefined;
export const InputView = component({
    provider: CoreConfig.provider,
    moduleName: CoreConfig.moduleName,
    componentName: Configuration.ComponentNames.input,
    style(theme, mediaQuery, window, props) {
        var _a, _b, _c;
        return {
            after: Object.assign({ flexDirection: 'row', alignItems: 'center', height: '100%', borderStyle: 'solid', padding: 15, backgroundColor: '#F0F3F7' }, getDirection().value === 'rtl' && Platform.OS === 'web'
                ? {
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    borderRightWidth: 1,
                }
                : {
                    borderLeftWidth: 1,
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8
                }),
            before: Object.assign({ flexDirection: 'row', alignItems: 'center', height: '100%', borderStyle: 'solid', padding: 15, backgroundColor: '#F0F3F7' }, getDirection().value === 'rtl' && Platform.OS === 'web'
                ? {
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                    borderLeftWidth: 1,
                }
                : {
                    borderRightWidth: 1,
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8
                }),
            container: {
                position: 'relative',
                paddingTop: 5
            },
            inputContainer: {
                borderRadius: 8,
                borderColor: ((_a = theme.colors) === null || _a === void 0 ? void 0 : _a.link_color) || 'rgb(191, 204, 218)',
                borderWidth: 1,
                borderStyle: 'solid',
                marginVertical: 1,
                marginHorizontal: 3
            },
            inputContainerFocused: {
                borderColor: ((_b = theme.colors) === null || _b === void 0 ? void 0 : _b.secondary_color) || 'rgb(17, 90, 201)'
            },
            inputContainerHovered: {
                borderColor: ((_c = theme.colors) === null || _c === void 0 ? void 0 : _c.secondary_color) || 'rgb(17, 90, 201)'
            },
            input: {
                flexDirection: 'row',
                alignItems: 'center'
            },
            textInput: {
                backgroundColor: '#fff',
                borderRadius: 8,
                minHeight: 48,
                flex: 1,
                zIndex: 2,
                minWidth: 0,
                height: '100%',
                paddingVertical: 0,
                paddingHorizontal: 15,
                fontSize: 16,
                maxHeight: 18,
                lineHeight: 18,
                color: 'rgb(115, 135, 159)',
                borderBottomWidth: 0,
                borderWidth: 0
            },
            inputWrapper: {
                flex: 1,
                minHeight: 48,
                padding: 15
            },
            textInputDisabled: {
                color: '#BFCCDA'
            },
            textInputHovered: {
                color: '#394E66'
            },
            textInputFocused: {
                color: '#394E66'
            },
            label: {
                borderRadius: 3,
                overflow: 'hidden',
                zIndex: 1,
                lineHeight: 18,
                fontSize: 16,
                color: 'rgba(115, 135, 159, 1)',
                paddingHorizontal: 2,
                backgroundColor: '#fff',
                position: 'absolute'
            },
            clearBtn: {
                marginTop: Platform.select({
                    web: -4
                }),
                paddingHorizontal: 15,
                flex: undefined
            },
            hint: {
                fontSize: 12,
                lineHeight: 24,
                marginVertical: 5,
                paddingStart: 15,
                color: '#4F657E'
            },
            disabled: {},
            errorText: Object.assign(Object.assign({}, !props.noErrorPlaceholder && { minHeight: 25 }), { color: 'rgba(255, 0, 0, 1)' }),
            innerHint: {
                paddingStart: 15,
                color: '#4F657E',
                padding: 12,
                fontSize: 14,
                lineHeight: 20,
                marginVertical: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                backgroundColor: '#F0F3F7'
            }
        };
    }
})((_a) => {
    var _b, _c, _d, _e, _f, _g, _h;
    var { style, testID, onClear, transformValue, ignoreNumberValidation, beforeStyle, afterStyle, keyboardType, containerStyle, validateOnInput, extError, validateOnBlur, inputStyle, label, onChangeText, placeholder, floating, textColor, clear, before, after, hint, innerHint, verify, disabled, value } = _a, otherProps = __rest(_a, ["style", "testID", "onClear", "transformValue", "ignoreNumberValidation", "beforeStyle", "afterStyle", "keyboardType", "containerStyle", "validateOnInput", "extError", "validateOnBlur", "inputStyle", "label", "onChangeText", "placeholder", "floating", "textColor", "clear", "before", "after", "hint", "innerHint", "verify", "disabled", "value"]);
    const inputRef = useRef();
    const focusTime = useRef(0);
    const [dir] = useDirection();
    const [text, setText] = useState(typeof value === 'number' || typeof value === 'string' ? `${value}` : '');
    const [labelLeft, setLabelLeft] = useState(15);
    const [error, setError] = useState('');
    const [isHover, setIsHover] = useState(false);
    const [isFocus, setIsFocus] = useState(false);
    const isFocusRef = useRef(false);
    const [color, setColor] = useAnimation({ initialValue: 0, duration: 100, nativeDriver: false });
    const [textAnimatedColor, setTextAnimatedColor] = useAnimation({ initialValue: 0, duration: 100, nativeDriver: false });
    const [top, setTop] = useAnimation({ initialValue: (value || placeholder) ? TopPosition : CenterPosition, duration: 200, nativeDriver: false });
    const [scale, setScale] = useAnimation({ initialValue: (value || placeholder) && label ? 0.9 : 1, duration: 200, nativeDriver: false });
    const [errorAnimation, setErrorAnimation] = useAnimation({ initialValue: 0, duration: 250 });
    const handleChangeText = useCallback((str) => {
        const numberTypes = [
            'number-pad',
            'decimal-pad',
            'numeric'
        ];
        if (!ignoreNumberValidation && Platform.OS === 'web' && numberTypes.includes(keyboardType || '')) {
            if (isNaN(Number(str))) {
                return;
            }
        }
        if (transformValue) {
            str = transformValue(str);
        }
        setText(str);
        if (!!`${str}`) {
            setTop(TopPosition);
            setScale(0.9);
        }
        if (validateOnBlur) {
            onChangeText === null || onChangeText === void 0 ? void 0 : onChangeText(str);
            return;
        }
        if (!validateOnInput) {
            onChangeText === null || onChangeText === void 0 ? void 0 : onChangeText(str);
            return;
        }
        const error = typeof verify === 'function' && verify(str);
        setError(error || '');
        setErrorAnimation(error ? 1 : 0);
        error && setColor(2);
        error && setTextAnimatedColor(2);
        !error && isFocus && setColor(1);
        !error && isFocus && setTextAnimatedColor(1);
        onChangeText === null || onChangeText === void 0 ? void 0 : onChangeText(str);
    }, [onChangeText, error, verify, isFocus, transformValue, ignoreNumberValidation, keyboardType, validateOnBlur, validateOnInput]);
    useEffect(() => {
        setError(extError || '');
        setErrorAnimation(extError ? 1 : 0);
        extError && setColor(2);
        extError && setTextAnimatedColor(2);
        if (!extError) {
            if (isFocus) {
                setColor(1);
                setTextAnimatedColor(1);
            }
            else {
                setColor(0);
                setTextAnimatedColor(0);
            }
        }
    }, [extError]);
    useEffect(() => {
        text != value && handleChangeText(typeof value === 'number' || typeof value === 'string' ? `${value}` : '');
        if (!value && !isFocusRef.current && !placeholder) {
            setTop(CenterPosition);
            setScale(1);
        }
    }, [value, placeholder, handleChangeText]);
    const handleHover = useCallback(() => {
        if (disabled) {
            return;
        }
        setIsHover(true);
        !error && setColor(1);
    }, [error]);
    const handleHoverOut = useCallback(() => {
        if (disabled) {
            return;
        }
        setIsHover(false);
        !isFocus && !error && setColor(0);
        !isFocus && !error && setTextAnimatedColor(0);
    }, [error, isFocus]);
    const handleFocus = useCallback((e) => {
        var _a, _b;
        focusedRef = {
            props: {
                testID: `input-${testID || label || placeholder || ''}`,
                keyboardType,
                label,
                placeholder,
                textContentType: otherProps.textContentType
            }
        };
        focusTime.current = new Date().getTime();
        if (otherProps.disableAutofocus) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            (_b = otherProps.onFocus) === null || _b === void 0 ? void 0 : _b.call(otherProps, e);
            return;
        }
        if (disabled) {
            return;
        }
        !error && setColor(1);
        !error && setTextAnimatedColor(1);
        setTop(TopPosition);
        setScale(0.9);
        setIsFocus(true);
        isFocusRef.current = true;
        return typeof otherProps.onFocus === 'function' && otherProps.onFocus(e);
    }, [otherProps.onFocus, error, disabled]);
    const handleBlur = useCallback((e) => {
        var _a;
        focusedRef = undefined;
        const time = new Date().getTime();
        const delta = time - (focusTime.current || 0);
        const delay = 300;
        if (delta < delay) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        if (otherProps.disableAutofocus) {
            return;
        }
        if (disabled) {
            return;
        }
        if (validateOnBlur) {
            const error = typeof verify === 'function' && verify(text);
            setError(error || '');
            setErrorAnimation(error ? 1 : 0);
            error && setColor(2);
            error && setTextAnimatedColor(2);
        }
        else {
            !error && setColor(0);
            !error && setTextAnimatedColor(0);
        }
        setIsFocus(false);
        isFocusRef.current = false;
        if (!text && !placeholder) {
            setTop(CenterPosition);
            setScale(1);
        }
        return typeof otherProps.onBlur === 'function' && otherProps.onBlur(e);
    }, [value, text, error, placeholder, disabled, otherProps.disableAutofocus, validateOnBlur, verify, otherProps.onBlur]);
    const manualFocus = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [inputRef.current]);
    const handleClear = useCallback(() => {
        if (disabled) {
            return;
        }
        handleChangeText('');
        typeof onClear === 'function' && onClear();
        setTimeout(manualFocus, 100);
    }, [otherProps.onChange, clear, manualFocus, handleChangeText]);
    const handleBeforeLayout = useCallback((e) => {
        setLabelLeft(15 + e.nativeEvent.layout.width);
    }, []);
    useEffect(() => {
        var _a;
        (_a = otherProps.getRef) === null || _a === void 0 ? void 0 : _a.call(otherProps, {
            blur: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur(); },
            focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
            setValue: (value) => {
                setText(value);
                return value;
            }
        });
    }, [inputRef.current]);
    const borderColor = color.interpolate({
        inputRange: [0, 1, 2],
        outputRange: [(_b = style === null || style === void 0 ? void 0 : style.inputContainer) === null || _b === void 0 ? void 0 : _b.borderColor, (_c = style === null || style === void 0 ? void 0 : style.inputContainerFocused) === null || _c === void 0 ? void 0 : _c.borderColor, 'rgb(255, 0, 0)']
    });
    const inputStyles = [
        style === null || style === void 0 ? void 0 : style.textInput,
        inputStyle,
        isHover && !isFocus && (style === null || style === void 0 ? void 0 : style.textInputHovered),
        (isFocus || before || after || text !== '') && (style === null || style === void 0 ? void 0 : style.textInputFocused),
        disabled && (style === null || style === void 0 ? void 0 : style.textInputDisabled),
        { shadowOpacity: 0, shadowRadius: 0 },
        dir === 'rtl' && { direction: 'rtl', textAlign: 'right' },
    ];
    const inputContainerStyle = Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.inputContainer), Platform.select({
        default: {
            borderColor,
            backgroundColor: '#fff'
        },
        ios: Object.assign({ borderColor, backgroundColor: '#fff' }, isFocus && {
            shadowColor: borderColor,
            shadowOpacity: 1,
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowRadius: 1
        }),
        web: Object.assign({ borderColor, backgroundColor: '#fff' }, isFocus && {
            shadowColor: borderColor,
            shadowOpacity: 1,
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowRadius: 2
        })
    }));
    if (Platform.OS === 'web') {
        inputStyles.push({ outlineStyle: 'none', backgroundClip: 'text' });
    }
    const labelStyles = [
        style === null || style === void 0 ? void 0 : style.label,
        Object.assign(Object.assign({}, dir === 'rtl' && Platform.OS === 'web'
            ? { right: labelLeft }
            : { left: labelLeft }), { color: textAnimatedColor.interpolate({
                inputRange: [0, 1, 2],
                outputRange: [(_d = style === null || style === void 0 ? void 0 : style.label) === null || _d === void 0 ? void 0 : _d.color, (_e = style === null || style === void 0 ? void 0 : style.inputContainerFocused) === null || _e === void 0 ? void 0 : _e.borderColor, 'rgb(255, 0, 0)']
            }), transform: [
                { translateY: top },
                { scale }
            ] })
    ];
    let placeholderColor = (_f = style === null || style === void 0 ? void 0 : style.textInput) === null || _f === void 0 ? void 0 : _f.color;
    if (disabled) {
        placeholderColor = (_g = style === null || style === void 0 ? void 0 : style.textInputDisabled) === null || _g === void 0 ? void 0 : _g.color;
        labelStyles.push({
            color: (_h = style === null || style === void 0 ? void 0 : style.textInputDisabled) === null || _h === void 0 ? void 0 : _h.color
        });
    }
    const Input = TextInput;
    const AnimatedText = AnimatedTextView;
    const Container = otherProps.editable === false && Platform.OS !== 'web'
        ? AnimatedPressable
        : Animated.View;
    return (_jsxs(Animated.View, { style: [style === null || style === void 0 ? void 0 : style.container, containerStyle], children: [!!label && (_jsx(AnimatedText, { textStyle: labelStyles, onPress: otherProps.editable === false && Platform.OS !== 'web'
                    ? handleFocus
                    : manualFocus, onMouseEnter: handleHover, onMouseLeave: handleHoverOut, children: label })), _jsxs(Container, Object.assign({ style: inputContainerStyle }, otherProps.editable === false && Platform.OS !== 'web' && {
                onPress: handleFocus
            }, { children: [_jsxs(View, Object.assign({ style: style === null || style === void 0 ? void 0 : style.input, pointerEvents: otherProps.editable === false && Platform.OS !== 'web' ? 'none' : undefined }, dir === 'rtl' && { dir: 'rtl' }, { children: [!!before && (_jsx(Animated.View, { onLayout: handleBeforeLayout, style: [
                                    style === null || style === void 0 ? void 0 : style.before,
                                    { borderColor },
                                    beforeStyle
                                ], children: before() })), _jsx(Input, Object.assign({}, otherProps, Platform.OS === 'android' && {
                                blurOnSubmit: otherProps.blurOnSubmit || false
                            }, dir === 'rtl' && { dir: 'rtl' }, otherProps.multiline && !otherProps.textAlignVertical && { textAlignVertical: 'top' }, { testID: `input-${testID || label || placeholder || ''}`, keyboardType: keyboardType, onChangeText: handleChangeText, ref: inputRef, placeholder: placeholder, placeholderTextColor: placeholderColor, value: text, style: inputStyles, onFocus: handleFocus, onBlur: handleBlur, onMouseEnter: handleHover, onMouseLeave: handleHoverOut, editable: typeof otherProps.editable === 'boolean'
                                    ? otherProps.editable
                                    : !disabled, allowFontScaling: false, caretHidden: false, autoCorrect: typeof otherProps.autoCorrect === 'boolean' ? otherProps.autoCorrect : false, autoCapitalize: otherProps.autoCapitalize ? otherProps.autoCapitalize : 'none' })), !!clear && !!text && !disabled && (_jsx(DenseButtonView, { wrapperStyle: style === null || style === void 0 ? void 0 : style.clearBtn, hitSlop: 5, containerStyle: { width: undefined, flex: undefined, minHeight: 48 }, onPress: handleClear, children: _jsx(IconComponent, { name: "si-close-cancel", size: 10 }) })), !!after && (_jsx(AnimatedPressable, { style: [
                                    style === null || style === void 0 ? void 0 : style.after,
                                    { borderColor },
                                    afterStyle
                                ], onPress: otherProps.editable === false && Platform.OS !== 'web'
                                    ? handleFocus
                                    : manualFocus, children: after() }))] })), !!innerHint && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.innerHint, children: innerHint }))] })), !error && !!hint && (_jsx(AnimatedTextView, { textStyle: style === null || style === void 0 ? void 0 : style.hint, children: hint })), !hint && !!error && (_jsx(AnimatedTextView, { textStyle: [
                    style === null || style === void 0 ? void 0 : style.hint,
                    style === null || style === void 0 ? void 0 : style.errorText,
                    { opacity: errorAnimation }
                ], children: error }))] }));
});
